import React, { useState, useEffect, useRef } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Typography, 
  Box,
  CircularProgress,
  Divider,
  FormControlLabel,
  Checkbox,
  Alert,
  Tooltip
} from '@mui/material';
import { useLanguage } from '../context/LanguageContext';
import api from '../api';
import ReactMarkdown from 'react-markdown';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import logger from './../utils/logger.js';

// Embed disclaimer content directly in the component
const DISCLAIMER_CONTENT = `## Disclaimer – Gebruik van Salinator

**Salinator – AI Powered Outreach for Sales** is ontworpen om gebruikers te helpen bij het identificeren en benaderen van potentiële klanten door middel van slimme analyses op basis van openbare DNS-gegevens en AI-gegenereerde bedrijfsinformatie. Hoewel we streven naar een zo accuraat en nuttig mogelijk beeld, is het belangrijk om onderstaande beperkingen en aandachtspunten in acht te nemen:

### 1. Analyse op basis van DNS-gegevens

Voor elk opgegeven domein verzamelen en interpreteren wij publiek toegankelijke DNS-records, waaronder:

* **MX-records**: hiermee bepalen we of het e-mailadres gekoppeld is aan Microsoft 365, Google Workspace of een andere provider.

* **SPF-, DKIM- en DMARC-records**: deze bieden inzicht in e-mailverzending en -beveiliging, en kunnen indirect wijzen op de gebruikte e-mailinfrastructuur. Let op:

  * DKIM wordt slechts herkend via een bekende set selectors; deze lijst is niet volledig. Afwezigheid van een gevonden DKIM-record betekent dus niet per definitie dat DKIM niet is ingesteld.
  * Een correct ingestelde DMARC-record wijst op bescherming tegen e-mailspoofing, maar is geen garantie voor volledige veiligheid.

* **TXT site-verification records**: uit deze informatie leiden wij mogelijk af welke externe diensten (zoals CRM, analytics, marketing tools) door het bedrijf zijn (of waren) gekoppeld. Let op:

  * Verificatie-records kunnen verouderd zijn of verwijderd zijn na initiële verificatie. Het al dan niet aanwezig zijn van zo'n record is dus geen sluitend bewijs voor huidig gebruik van een dienst.

### 2. AI-gebaseerde Bedrijfsinformatie

Salinator gebruikt AI om op basis van publiek beschikbare bedrijfsgegevens en websites een puntsgewijze samenvatting te genereren met potentiële gesprekshaakjes of relevante achtergrondinformatie. Houd hierbij rekening met:

* De AI-output is gegenereerd op basis van informatie uit externe bronnen die mogelijk niet actueel of volledig accuraat zijn.
* Er kunnen onjuiste verbanden worden gelegd of verkeerde interpretaties ontstaan.
* Gebruikers wordt **ten zeerste aangeraden altijd de bijgevoegde bronnen (citations)** te raadplegen en zelf een laatste controle uit te voeren voordat informatie wordt gebruikt in communicatie met een prospect.

---

Door Salinator te gebruiken accepteer je dat de geboden informatie een hulpmiddel is, en geen gegarandeerde weergave van de werkelijkheid vormt. Het blijft de verantwoordelijkheid van de gebruiker om informatie juist te interpreteren en correct toe te passen in de praktijk.`;

// Dutch translations for hardcoding if needed
const DUTCH_TRANSLATIONS = {
  disclaimer: "Disclaimer",
  pleaseReadAndAccept: "Lees en accepteer de disclaimer voordat je de applicatie gebruikt",
  iAcknowledgeAndAccept: "Ik bevestig dat ik de disclaimer heb gelezen en de voorwaarden accepteer",
  accept: "Accepteren",
  disclaimerRequired: "Je moet de disclaimer accepteren om deze applicatie te gebruiken",
  scrollToBottom: "Scroll naar beneden om de volledige disclaimer te lezen"
};

const DisclaimerDialog = ({ open, onClose, onAccept }) => {
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [agreedChecked, setAgreedChecked] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const contentRef = useRef(null);
  
  // Debug logging for translations
  useEffect(() => {
    logger.log("DisclaimerDialog: Current language =", language);
    logger.log("DisclaimerDialog: 'disclaimer' translation =", t('disclaimer'));
    logger.log("DisclaimerDialog: 'pleaseReadAndAccept' translation =", t('pleaseReadAndAccept'));
    logger.log("DisclaimerDialog: 'iAcknowledgeAndAccept' translation =", t('iAcknowledgeAndAccept'));
    logger.log("DisclaimerDialog: 'accept' translation =", t('accept'));
    logger.log("DisclaimerDialog: 'disclaimerRequired' translation =", t('disclaimerRequired'));
  }, [language, t]);

  // Reset the scrolled state when the dialog is reopened
  useEffect(() => {
    if (open) {
      setHasScrolledToBottom(false);
      setAgreedChecked(false);
    }
  }, [open]);

  // Function to get translations - tries t function first, falls back to hardcoded Dutch
  const getTranslation = (key) => {
    const translation = t(key);
    // If translation is the same as key, it means the translation failed
    if (translation === key && language === 'nl') {
      return DUTCH_TRANSLATIONS[key] || key;
    }
    return translation;
  };

  const handleAccept = async () => {
    if (!agreedChecked) {
      setError(getTranslation('disclaimerRequired'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/accept-disclaimer');
      if (response.data.success) {
        if (onAccept) {
          onAccept();
        }
        onClose();
      } else {
        setError(response.data.message || 'Failed to accept disclaimer');
      }
    } catch (err) {
      logger.error('Error accepting disclaimer:', err);
      setError(err.response?.data?.message || 'An error occurred while accepting the disclaimer');
    } finally {
      setLoading(false);
    }
  };

  const handleAgreeChange = (event) => {
    setAgreedChecked(event.target.checked);
    if (event.target.checked) {
      setError('');
    }
  };

  const handleScroll = (e) => {
    if (!hasScrolledToBottom) {
      const element = e.target;
      // Check if scrolled to bottom (with a small tolerance)
      const isAtBottom = 
        Math.abs(element.scrollHeight - element.clientHeight - element.scrollTop) < 10;
      
      if (isAtBottom) {
        setHasScrolledToBottom(true);
        logger.log('User has scrolled to the bottom of the disclaimer');
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={null} // Prevent closing by clicking outside
      maxWidth="lg" // Increased from "md" to "lg" for larger dialog
      fullWidth
      aria-labelledby="disclaimer-dialog-title"
      PaperProps={{
        sx: {
          width: '85%', // Make dialog wider
          height: '85vh', // Make dialog taller
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle id="disclaimer-dialog-title">
        <Typography variant="h5" component="div">
          {getTranslation('disclaimer')}
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          {getTranslation('pleaseReadAndAccept')}
        </Typography>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Box
          ref={contentRef}
          sx={{
            mb: 2,
            maxHeight: '60vh',
            overflowY: 'auto',
            p: 2,
            position: 'relative',
            border: '1px solid #e0e0e0',
            borderRadius: 1
          }}
          onScroll={handleScroll}
        >
          <ReactMarkdown>{DISCLAIMER_CONTENT}</ReactMarkdown>
          
          {!hasScrolledToBottom && (
            <Box
              sx={{
                position: 'sticky',
                bottom: 0,
                width: '100%',
                backgroundColor: 'rgba(255,255,255,0.9)',
                p: 1,
                textAlign: 'center',
                borderTop: '1px solid #e0e0e0'
              }}
            >
              <Typography variant="body2" color="text.secondary">
                <KeyboardDoubleArrowDownIcon /> {getTranslation('scrollToBottom')}
              </Typography>
            </Box>
          )}
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Tooltip
          title={!hasScrolledToBottom ? getTranslation('scrollToBottom') : ''}
          placement="top"
          arrow
        >
          <span>
            <FormControlLabel
              control={
                <Checkbox 
                  checked={agreedChecked}
                  onChange={handleAgreeChange}
                  color="primary"
                  disabled={!hasScrolledToBottom}
                />
              }
              label={getTranslation('iAcknowledgeAndAccept')}
              sx={!hasScrolledToBottom ? { opacity: 0.6 } : {}}
            />
          </span>
        </Tooltip>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleAccept}
          disabled={loading || !agreedChecked || !hasScrolledToBottom}
          size="large"
        >
          {loading ? <CircularProgress size={24} /> : getTranslation('accept')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DisclaimerDialog; 